import { axiosClient } from "../api/axiosClient";
import type {
  CurrentPlanResponse,
  FeatureLimitCheckResponse,
  NumericFeatureKey,
  PlanFeatureLimits,
  SubscriptionFeaturesResponse,
  SubscriptionTier,
  SubscriptionUsageResponse,
  EnhancedSubscriptionFeaturesResponse,
} from "../types/subscription.types";

export interface SubscriptionCheckoutPayload {
  priceId?: string;
  plan?: string;
  interval?: "month" | "year";
  terreiroId?: string;
  mediumId?: string;
}

export interface SubscriptionCheckoutResponse {
  url: string;
  sessionId: string;
}

export interface PortalSessionPayload {
  returnUrl?: string;
}

export interface PortalSessionResponse {
  url: string;
}

export interface SubscriptionResponse {
  id: string;
  priceId: string;
  status: string;
  current_period_end?: string | null;
  trial_end?: string | null;
  cancel_at_period_end?: boolean;
  planInterval?: "month" | "year" | null;
  isPartner?: boolean;
  tier?: string;
  planName?: string;
  [key: string]: unknown;
}

type UsageSummaryApiFeature = {
  used?: number;
  limit?: number;
};

type UsageSummaryApiResponse = {
  plan?: string;
  features?: Record<string, UsageSummaryApiFeature>;
};

const toUsageStat = (feature?: UsageSummaryApiFeature) => {
  const hasUsed = typeof feature?.used === "number";
  const hasLimit = typeof feature?.limit === "number";

  const current = hasUsed ? (feature?.used as number) : 0;
  const limit = hasLimit ? (feature?.limit as number) : 0;
  const percentage =
    limit === -1 ? 0 : limit === 0 ? 100 : Math.round((current / limit) * 100);

  return {
    current,
    limit,
    percentage,
    nearingLimit: limit !== -1 && percentage >= 80,
  };
};

const normalizeCurrentPlanToFeatures = (
  data: CurrentPlanResponse | Record<string, unknown>,
): SubscriptionFeaturesResponse => {
  const isSubscriptionTier = (value: unknown): value is SubscriptionTier => {
    return (
      typeof value === "string" &&
      [
        "free",
        "essential",
        "management",
        "complete",
        "basic",
        "standard",
        "advanced",
        "unlimit",
        "enterprise",
        "personal_free",
        "personal_basic",
        "personal_partner",
      ].includes(value)
    );
  };

  const safeData = (data ?? {}) as {
    currentPlan?: {
      tier?: SubscriptionTier;
      features?: Partial<PlanFeatureLimits>;
    };
    tier?: SubscriptionTier;
    plan?: string;
    features?: Partial<PlanFeatureLimits>;
  };

  const tierFromPayload =
    safeData.currentPlan?.tier ??
    (isSubscriptionTier(safeData.tier) ? safeData.tier : undefined) ??
    (isSubscriptionTier(safeData.plan) ? safeData.plan : undefined);

  const tier = tierFromPayload ?? "free";

  const planFeatures =
    safeData.currentPlan?.features ?? safeData.features ?? {};

  const toNumberLimit = (value: unknown, fallback = -1): number => {
    return typeof value === "number" ? value : fallback;
  };

  const toBooleanFeature = (value: unknown, fallback = false): boolean => {
    return typeof value === "boolean" ? value : fallback;
  };

  return {
    tier,
    features: {
      maxEvents: toNumberLimit(planFeatures.maxEventsPerMonth),
      maxMembers: toNumberLimit(planFeatures.maxMediums),
      maxActivities: toNumberLimit(planFeatures.maxActivitiesPerMonth),
      maxInventoryItems: toNumberLimit(planFeatures.maxInventoryItems),
      maxBankAccounts: toNumberLimit(planFeatures.maxBankAccounts),
      maxIncomingTransactions: toNumberLimit(planFeatures.maxIncomesPerMonth),
      maxOutcomeTransactions: toNumberLimit(planFeatures.maxOutcomesPerMonth),
      maxRitualBaths: toNumberLimit(planFeatures.maxRitualBaths),
      advancedReports: toBooleanFeature(planFeatures.canUseAdvancedReports),
      customBranding: false,
      apiAccess: false,
      prioritySupport: toBooleanFeature(planFeatures.prioritySupport),
      dataExport: toBooleanFeature(planFeatures.canExportData),
      slackIntegration: toBooleanFeature(planFeatures.canUseSlackIntegration),
      canUseTemplates: toBooleanFeature(planFeatures.canUseTemplates),
      canManageFinancials: toBooleanFeature(planFeatures.canManageFinancials),
      canUseWhatsAppNotifications: toBooleanFeature(
        planFeatures.canUseWhatsAppNotifications,
      ),
    },
  };
};

const normalizeUsageSummary = (
  data: UsageSummaryApiResponse,
): SubscriptionUsageResponse => {
  const features = data.features ?? {};

  return {
    tier: (data.plan ?? "free") as SubscriptionUsageResponse["tier"],
    usage: {
      events: toUsageStat(features.maxEventsPerMonth),
      members: toUsageStat(features.maxMediums),
      activities: toUsageStat(features.maxActivitiesPerMonth),
      inventoryItems: toUsageStat(features.maxInventoryItems),
      bankAccounts: toUsageStat(features.maxBankAccounts),
      incomingTransactions: toUsageStat(features.maxIncomesPerMonth),
      outcomeTransactions: toUsageStat(features.maxOutcomesPerMonth),
      ritualBaths: toUsageStat(features.maxRitualBaths),
    },
  };
};

export const subscriptionRepository = {
  async checkout(
    payload: SubscriptionCheckoutPayload,
  ): Promise<SubscriptionCheckoutResponse> {
    const { data } = await axiosClient.post<SubscriptionCheckoutResponse>(
      "/subscription/checkout",
      payload,
      { withCredentials: true },
    );
    return data;
  },

  async changePlan(payload: {
    plan: string;
    interval?: "month" | "year";
    terreiroId: string;
    scheduled?: boolean;
  }): Promise<{ scheduled: boolean; subscription: SubscriptionResponse }> {
    const path = payload.scheduled ? "/subscription/downgrade" : "/subscription/upgrade";
    const { data } = await axiosClient.post<{
      scheduled: boolean;
      subscription: SubscriptionResponse;
    }>(path, payload, { withCredentials: true });
    return data;
  },

  async reactivate(payload: { terreiroId: string }): Promise<SubscriptionResponse> {
    const { data } = await axiosClient.post<SubscriptionResponse>(
      "/subscription/reactivate",
      payload,
      { withCredentials: true },
    );
    return data;
  },

  async getCurrentSubscription(
    terreiroId?: string,
    mediumId?: string,
  ): Promise<SubscriptionResponse | null> {
    try {
      const { data } = await axiosClient.get<SubscriptionResponse>(
        `/subscription`,
        {
          withCredentials: true,
          params: {
            ...(terreiroId && { terreiroId }),
            ...(mediumId && { mediumId }),
          },
        },
      );
      // If data is empty, treat as no subscription
      if (!data || !data.id) return null;
      return data;
    } catch (error: unknown) {
      // Type guard for AxiosError
      if (
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        typeof (error as { response?: { status?: number } }).response ===
          "object" &&
        (error as { response?: { status?: number } }).response?.status === 404
      ) {
        return null;
      }
      throw error;
    }
  },

  async createPortalSession(
    payload?: PortalSessionPayload,
  ): Promise<PortalSessionResponse> {
    const { data } = await axiosClient.post<PortalSessionResponse>(
      "/subscription/portal-session",
      payload ?? {},
      { withCredentials: true },
    );
    return data;
  },

  /**
   * Get current plan tier and features for the authenticated user/terreiro/medium
   * @param terreiroId - Optional terreiro ID to check features for
   * @param mediumId - Optional medium ID to check features for (personal subscription)
   * @returns Features available based on subscription tier
   */
  async getFeatures(
    terreiroId?: string,
    mediumId?: string,
  ): Promise<SubscriptionFeaturesResponse> {
    const { data } = await axiosClient.get<CurrentPlanResponse>(
      "/subscription/feature-access",
      {
        withCredentials: true,
        params: {
          ...(terreiroId && { terreiroId }),
          ...(mediumId && { mediumId }),
        },
      },
    );
    return normalizeCurrentPlanToFeatures(data);
  },

  async getCurrentPlan(
    terreiroId?: string,
    mediumId?: string,
  ): Promise<CurrentPlanResponse> {
    const { data } = await axiosClient.get<CurrentPlanResponse>(
      "/subscription/current-plan",
      {
        withCredentials: true,
        params: {
          ...(terreiroId && { terreiroId }),
          ...(mediumId && { mediumId }),
        },
      },
    );

    return data;
  },

  /**
   * Check if a specific feature is accessible before attempting creation
   * @param feature - Feature key to check (e.g., 'maxEvents', 'maxMembers')
   * @param terreiroId - Optional terreiro ID to check for
   * @param mediumId - Optional medium ID to check for (personal subscription)
   * @param currentCount - Optional current count if already known (avoids extra DB query)
   * @returns Limit check result with allowed status and usage info
   */
  async checkLimit(
    feature: NumericFeatureKey,
    terreiroId?: string,
    mediumId?: string,
    currentCount?: number,
  ): Promise<FeatureLimitCheckResponse> {
    const { data } = await axiosClient.get<FeatureLimitCheckResponse>(
      `/subscription/features/${feature}/check`,
      {
        withCredentials: true,
        params: {
          ...(terreiroId && { terreiroId }),
          ...(mediumId && { mediumId }),
          ...(currentCount !== undefined && { currentCount }),
        },
      },
    );
    return data;
  },

  /**
   * Get usage statistics for all features
   * @param params - Context parameters (terreiroId OR mediumId)
   * @returns Usage statistics for all countable features
   */
  async getUsage(params?: {
    terreiroId?: string;
    mediumId?: string;
  }): Promise<SubscriptionUsageResponse> {
    const { data } = await axiosClient.get<UsageSummaryApiResponse>(
      "/subscription/usage",
      {
        withCredentials: true,
        params: params
          ? {
              ...(params.terreiroId && { terreiroId: params.terreiroId }),
              ...(params.mediumId && { mediumId: params.mediumId }),
            }
          : undefined,
      },
    );
    return normalizeUsageSummary(data);
  },

  /**
   * Get enhanced subscription features with detailed limit information
   * This returns the new API format with limit, used, remaining, and allowed properties
   * @param terreiroId - Optional terreiro ID to check features for
   * @param mediumId - Optional medium ID to check features for (personal subscription)
   * @returns Enhanced features with detailed usage information
   */
  async getEnhancedFeatures(
    terreiroId?: string,
    mediumId?: string,
  ): Promise<EnhancedSubscriptionFeaturesResponse> {
    const { data } =
      await axiosClient.get<EnhancedSubscriptionFeaturesResponse>(
        "/subscription/current-plan",
        {
          withCredentials: true,
          params: {
            ...(terreiroId && { terreiroId }),
            ...(mediumId && { mediumId }),
          },
        },
      );
    return data;
  },
};
